import Map from "./Map";
import { COLORS, QUALITYSCORE_VALUES, GPS_VALUES } from "../../utils/utils";
import {
	MAP_EXPR_QUALITYSCORE, switchCoefValue, MAP_THEMES,
	mapFiltersToLayersFilters, MAP_EXPR_QUALITYSCORE_GPS
} from "../../utils/map";
import { SEMANTICS_OVERLAYS } from "../../utils/semantics";
import { SEMANTICS_OVERLAY_MINZOOM, SEMANTICS_OVERLAY_MAXZOOM } from "../../utils/SemanticsMapProtocol";
import { getUserAccount } from "../../utils/utils";

export const MAP_FILTERS = [ "minDate", "maxDate", "pic_type", "camera", "theme", "qualityscore", "gps"];


/**
 * MapMore is a more complete version of [Map UI component](#Panoramax.components.ui.Map).
 * 
 * It offers advanced features like themes, filters and more.
 * 
 * Note that all functions of [MapLibre GL JS class Map](https://maplibre.org/maplibre-gl-js/docs/API/classes/Map/) are also available.
 * 
 * ⚠️ This class doesn't inherit from [EventTarget](https://developer.mozilla.org/fr/docs/Web/API/EventTarget), so it doesn't have `addEventListener` and `dispatchEvent` functions.
 * It uses instead [`on`](https://maplibre.org/maplibre-gl-js/docs/API/classes/Map/#on) and `fire` functions from MapLibre Map class.
 * `fire` function doesn't take directly [`Event`](https://developer.mozilla.org/fr/docs/Web/API/Event) objects, but a string and object data.
 * @class Panoramax.components.ui.MapMore
 * @extends Panoramax.components.ui.Map
 * @param {Panoramax.components.core.Basic} parent The parent view
 * @param {Element} container The DOM element to create into
 * @param {object} [options] The map options (any of [MapLibre GL settings](https://maplibre.org/maplibre-gl-js/docs/API/type-aliases/MapOptions/) or any supplementary option defined here)
 * @param {object} [options.raster] The MapLibre raster source for aerial background. This must be a JSON object following [MapLibre raster source definition](https://maplibre.org/maplibre-style-spec/sources/#raster).
 * @param {string} [options.background=streets] Choose default map background to display (streets or aerial, if raster aerial background available). Defaults to streets.
 * @param {string} [options.attributionControl.customAttribution] To override default map attribution.
 * @param {boolean} [options.picMarkerDraggable] To make the picture marker draggable, default to false.
 * @param {object} [options.indoor] The indoor= MapLibre plugin options. This must be a JSON object following [IndoorEqual parameters](https://indoorequal.com/doc/maplibre-gl-indoorequal/api#parameters). Note that this is only available if [maplibre-gl-indoorequal](https://indoorequal.com/doc/maplibre-gl-indoorequal) plugin is loaded in your web page.
 * @param {string} [options.indoor.level] (only if indoor= plugin is enabled) The initial indoor level to display. Defaults to ground or disabled depending on indoor map availability.
 * @param {string} [options.theme=default] The map theme (default, age, score, type)
 * @fires Panoramax.components.ui.Map#background-changed
 * @fires Panoramax.components.ui.Map#users-changed
 * @fires Panoramax.components.ui.Map#sequence-hover
 * @fires Panoramax.components.ui.Map#sequence-click
 * @fires Panoramax.components.ui.Map#picture-click
 * @fires Panoramax.components.ui.MapMore#filters-changed
 * @example
 * const map = new Panoramax.components.ui.MapMore(viewer, mapNode, {center: {lat: 48.7, lng: -1.7}});
 */
export default class MapMore extends Map {
	constructor(parent, container, options = {}) {
		super(parent, container, options);

		// Map theme
		this._mapFilters = {};
		if(this._options.theme) { this._mapFilters = { theme: this._options.theme }; }

		// Features restrictions
		this._featuresRestrict = { pictures: {}, sequences: {} };
	}

	/** @private */
	async _postLoad() {
		if(this._parent.api._endpoints.queryables) {
			SEMANTICS_OVERLAYS.forEach(so => this.addSemanticOverlay(...so));
		}
		await super._postLoad();
	}

	reloadLayersStyles() {
		super.reloadLayersStyles();

		// Also handle the grid stats
		if(this._hasGridStats()) {
			let newType = "coef";
			if(this._mapFilters.pic_type) {
				newType = this._mapFilters.pic_type == "flat" ? "coef_flat_pictures" : "coef_360_pictures";
				if(this._hasLoggedGridStats() && getUserAccount()) {
					newType = "logged_" + newType;
				}
			}
			this.getStyle().layers
				.filter(l => l.id.endsWith("_grid"))
				.forEach(l => {
					const newl = switchCoefValue(l, newType);
					for(let p in newl.layout) {
						this.setLayoutProperty(l.id, p, newl.layout[p]);
					}
					for(let p in newl.paint) {
						this.setPaintProperty(l.id, p, newl.paint[p]);
					}
				});
		}
	}

	/**
	 * Computes dates to use for map theme by picture/sequence age
	 * @private
	 */
	_getDatesForLayerColors() {
		const oneDay = 24 * 60 * 60 * 1000;
		const d0 = Date.now();
		const d1 = d0 - 30 * oneDay;
		const d2 = d0 - 365 * oneDay;
		const d3 = d0 - 2 * 365 * oneDay;
		return [d1, d2, d3].map(d => new Date(d).toISOString().split("T")[0]);
	}

	/**
	 * Retrieve map layer color scheme according to selected theme.
	 * @private
	 */
	_getLayerColorStyle(layer) {
		// Hidden style
		const s = ["case",
			["==", ["get", "hidden"], true], COLORS.HIDDEN
		];

		// Selected sequence style
		const picId = this._parent.psv?._myVTour?.state?.loadingNode || this._parent.psv?._myVTour?.state?.currentNode?.id;
		const seqId = picId ? this._parent.psv?._picturesSequences[picId] : null;
		if(layer == "sequences" && seqId) {
			s.push(["==", ["get", "id"], seqId], COLORS.SELECTED);
		}
		else if(layer == "pictures" && seqId) {
			s.push(["in", seqId, ["get", "sequences"]], COLORS.SELECTED);
		}
		
		// Themes styles
		if(this._mapFilters.theme == MAP_THEMES.AGE) {
			const prop = layer == "sequences" ? "date" : "ts";
			const dt = this._getDatesForLayerColors();

			s.push(
				["!", ["has", prop]], COLORS.BASE,
				[">=", ["get", prop], dt[0]], COLORS.PALETTE_4,
				[">=", ["get", prop], dt[1]], COLORS.PALETTE_3,
				[">=", ["get", prop], dt[2]], COLORS.PALETTE_2,
				COLORS.PALETTE_1
			);
		}
		else if(this._mapFilters.theme == MAP_THEMES.TYPE) {
			s.push(
				["!", ["has", "type"]], COLORS.BASE,
				["==", ["get", "type"], "equirectangular"], COLORS.QUALI_1,
				COLORS.QUALI_2
			);
		}
		else if(this._mapFilters.theme == MAP_THEMES.SCORE) {
			s.push(
				["==", MAP_EXPR_QUALITYSCORE, 5], QUALITYSCORE_VALUES[0].color,
				["==", MAP_EXPR_QUALITYSCORE, 4], QUALITYSCORE_VALUES[1].color,
				["==", MAP_EXPR_QUALITYSCORE, 3], QUALITYSCORE_VALUES[2].color,
				["==", MAP_EXPR_QUALITYSCORE, 2], QUALITYSCORE_VALUES[3].color,
				QUALITYSCORE_VALUES[4].color,
			);
		}
		else if(this._mapFilters.theme == MAP_THEMES.GPS) {
			s.push(
				["==", MAP_EXPR_QUALITYSCORE_GPS, 5], GPS_VALUES[0].color,
				["==", MAP_EXPR_QUALITYSCORE_GPS, 4], GPS_VALUES[1].color,
				["==", MAP_EXPR_QUALITYSCORE_GPS, 3], GPS_VALUES[2].color,
				["==", MAP_EXPR_QUALITYSCORE_GPS, 2], GPS_VALUES[3].color,
				GPS_VALUES[4].color,
			);
		}
		else {
			s.push(COLORS.BASE);
		}

		return s;
	}

	/**
	 * Retrieve map sort key according to selected theme.
	 * @private
	 */
	_getLayerSortStyle(layer) {
		// Values
		//  - 100 : on top / selected feature
		//  - 90  : hidden feature
		//  - 20-80 : custom ranges
		//  - 10  : basic feature
		//  - 0   : on bottom / feature with undefined property

		// Hidden style
		const s = ["case",
			["==", ["get", "hidden"], true], 90
		];

		// Selected sequence style
		const picId = this._parent.psv?._myVTour?.state?.loadingNode || this._parent.psv?._myVTour?.state?.currentNode?.id;
		const seqId = picId ? this._parent.psv?._picturesSequences[picId] : null;
		if(layer == "sequences" && seqId) {
			s.push(["==", ["get", "id"], seqId], 100);
		}
		else if(layer == "pictures" && seqId) {
			s.push(["in", seqId, ["get", "sequences"]], 100);
		}

		// Themes styles
		if(this._mapFilters.theme == MAP_THEMES.AGE) {
			const prop = layer == "sequences" ? "date" : "ts";
			const dt = this._getDatesForLayerColors();
			s.push(
				["!", ["has", prop]], 0,
				[">=", ["get", prop], dt[0]], 50,
				[">=", ["get", prop], dt[1]], 49,
				[">=", ["get", prop], dt[2]], 48,
			);
		}
		else if(this._mapFilters.theme == MAP_THEMES.TYPE) {
			s.push(
				["!", ["has", "type"]], 0,
				["==", ["get", "type"], "equirectangular"], 50,
			);
		}
		else if(this._mapFilters.theme == MAP_THEMES.SCORE) {
			s.push(
				["==", MAP_EXPR_QUALITYSCORE, 5], 80,
				["==", MAP_EXPR_QUALITYSCORE, 4], 65,
				["==", MAP_EXPR_QUALITYSCORE, 3], 50,
				["==", MAP_EXPR_QUALITYSCORE, 2], 35,
				["==", MAP_EXPR_QUALITYSCORE, 1], 20,
			);
		}
		else if(this._mapFilters.theme == MAP_THEMES.GPS) {
			s.push(
				["==", MAP_EXPR_QUALITYSCORE_GPS, 5], 80,
				["==", MAP_EXPR_QUALITYSCORE_GPS, 4], 65,
				["==", MAP_EXPR_QUALITYSCORE_GPS, 3], 50,
				["==", MAP_EXPR_QUALITYSCORE_GPS, 2], 35,
				["==", MAP_EXPR_QUALITYSCORE_GPS, 1], 20,
			);
		}

		s.push(10);
		return s;
	}

	/**
	 * Change the map filters
	 * @param {object} filters Filtering values
	 * @param {string} [filters.minDate] Start date for pictures (format YYYY-MM-DD)
	 * @param {string} [filters.maxDate] End date for pictures (format YYYY-MM-DD)
	 * @param {string} [filters.pic_type] Type of picture to keep (flat, equirectangular)
	 * @param {string} [filters.camera] Camera make and model to keep
	 * @param {string} [filters.theme] Map theme to use
	 * @param {number[]} [filters.qualityscore] QualityScore values, as a list of 1 to 5 grades
	 * @param {number[]} [filters.gps] GPS precision grades, as a list of 1 to 5 grades
	 * @param {boolean} [skipZoomIn=false] If true, doesn't force zoom in to map level >= 7
	 * @memberof Panoramax.components.core.MapMore#
	 */
	setFilters(filters, skipZoomIn = false) {
		let { mapFilters, mapSeqFilters, mapPicFilters, reloadMapStyle } = mapFiltersToLayersFilters(filters, this._hasGridStats(), this._featuresRestrict);

		this._mapFilters = mapFilters;
		if(reloadMapStyle) {
			this.reloadLayersStyles();
		}

		const allUsers = this.getVisibleUsers().includes("geovisio");
		if(mapSeqFilters && allUsers) {
			mapSeqFilters = ["step", ["zoom"],
				true,
				7, mapSeqFilters
			];
		}

		this.filterUserLayersContent("sequences", mapSeqFilters);
		this.filterUserLayersContent("pictures", mapPicFilters);

		if(
			!skipZoomIn
			&& (
				mapSeqFilters !== null
				|| mapPicFilters !== null
				|| (this._mapFilters.theme !== null && this._mapFilters.theme !== MAP_THEMES.DEFAULT)
			)
			&& allUsers
			&& this.getZoom() < 7
			&& !this._hasGridStats()
		) {
			this.easeTo({ zoom: 7 });
		}

		/**
		 * Event for filters changes
		 * @event Panoramax.components.ui.MapMore#filters-changed
		 * @type {maplibregl.util.evented.Event}
		 * @property {string} [minDate] The minimum date in time range (ISO format)
		 * @property {string} [maxDate] The maximum date in time range (ISO format)
		 * @property {string} [pic_type] Camera type (equirectangular, flat, null/empty string for both)
		 * @property {string} [camera] Camera make and model
		 * @property {string} [theme] Map theme
		 * @property {number[]} [qualityscore] QualityScore values, as a list of 1 to 5 grades
		 * @property {number[]} [gps] GPS precision grades, as a list of 1 to 5 grades
		 */
		this.fire("filters-changed", Object.assign({}, this._mapFilters));
	}

	/**
	 * Restrict the visible pictures or sequences on map.
	 * @param {string} restriction "keep" or "hide"
	 * @param {string} type "pictures" or "sequences"
	 * @param {string[]} features The list of features UUIDs concerned by this rule
	 */
	restrictVisibleFeatures(restriction, type, features) {
		this._featuresRestrict[type] = { rule: restriction, features };

		// Force reload of styles & filters
		let { mapSeqFilters, mapPicFilters, reloadMapStyle } = mapFiltersToLayersFilters(this._mapFilters, this._hasGridStats(), this._featuresRestrict);
		if(reloadMapStyle) { this.reloadLayersStyles(); }
		this.filterUserLayersContent("sequences", mapSeqFilters);
		this.filterUserLayersContent("pictures", mapPicFilters);
	}

	/**
	 * Make given user layers visible on map, and hide all others (if any)
	 * @memberof Panoramax.components.ui.Map#
	 * @param {string|string[]} visibleIds The user layers IDs to display
	 */
	async setVisibleUsers(visibleIds = []) {
		await super.setVisibleUsers(visibleIds);
		
		// Force reload of styles & filters
		let { mapSeqFilters, mapPicFilters, reloadMapStyle } = mapFiltersToLayersFilters(this._mapFilters, this._hasGridStats(), this._featuresRestrict);

		if(reloadMapStyle) {
			this.reloadLayersStyles();
		}

		const allUsers = visibleIds.includes("geovisio");
		if(mapSeqFilters && allUsers) {
			mapSeqFilters = ["step", ["zoom"],
				true,
				7, mapSeqFilters
			];
		}

		this.filterUserLayersContent("sequences", mapSeqFilters);
		this.filterUserLayersContent("pictures", mapPicFilters);
	}

	/**
	 * Add a new semantic overlay on map
	 * @param {string} id The layer ID
	 * @param {string} apiFilter The API filter for semantics, [as documented in API](https://api.panoramax.xyz/redoc#tag/Items/operation/get_api-search-5769f3316ef291861415451c98c2e33f)
	 * @param {object} layerStyle The [MapLibre GL layer style](https://maplibre.org/maplibre-style-spec/layers/) to apply, including default visibility. Note that a "metadata" property is required to properly show it in Tags widget. If no metadata is set, layer is added as a "secret" layer (for internal mechanics).
	 * @param {object} [sprites] The optional sprites to load
	 * @memberof Panoramax.components.core.MapMore#
	 */
	addSemanticOverlay(id, apiFilter, layerStyle, sprites = {}) {
		if(!this._parent.api._endpoints.queryables) {
			throw new Error("Semantics overlay are not available on API");
		}

		// Sprites
		Object.entries(sprites || {}).forEach(([k,v]) => this.addSprite(k, v));

		// Overlay source & layer
		const srcId = "sem-"+id;
		this.addSource(srcId, {
			type: "vector",
			tiles: [`sem:///${apiFilter}/{z}/{x}/{y}`],
			minzoom: SEMANTICS_OVERLAY_MINZOOM,
			maxzoom: SEMANTICS_OVERLAY_MAXZOOM
		});
		this.addLayer({
			id,
			source: srcId,
			"source-layer": "layer",
			...layerStyle
		});

		// Click event
		this.on("click", id, e => {
			e.preventDefault();
			if(e?.features?.length > 0) {
				const f = e.features[0];
				this._parent.select(f.properties.seqid, f.properties.picid);
				this._parent.psv.addEventListener("picture-loaded", () => {
					const picMeta = this._parent.psv.getPictureMetadata();
					if(picMeta.properties?.annotations?.length === 1) {
						this._parent.psv.focusOnAnnotation(picMeta.properties.annotations[0].id);
					}
				}, { once: true });
			}
		});
	}

	/**
	 * List existing semantic overlays, either visible or not.
	 * @memberof Panoramax.components.core.MapMore#
	 * @returns {object[]} List of MapLibre layers
	 */
	getSemanticOverlays() {
		return this.getStyle().layers.filter(l => l.source?.startsWith("sem-"));
	}

	/**
	 * Change visibility of all semantics overlays at once.
	 * @param {boolean} visible True to make all visible
	 * @memberof Panoramax.components.core.MapMore#
	 */
	toggleAllSemanticOverlays(visible) {
		this.getSemanticOverlays().forEach(l => l.metadata && this.setLayoutProperty(l.id, "visibility", visible ? "visible" : "none"));
	}
}
